import type { Config } from "@bugsnag/js"

import { collectHydrationContext, isHydrationMismatchError } from "./hydration-context"
import {
  isIgnorableBrowserNoise,
  isInjectedScriptStackOverflow,
  isNodeWebstreamsTeardownError,
} from "./noise-filters"

/**
 * Read NEXT_PUBLIC_THEME directly instead of @/lib/theme/getThemeId() because this module
 * is imported by the client bundle, and @/lib/theme pulls in node:fs.
 */
function getThemeId(): string {
  return process.env.NEXT_PUBLIC_THEME || "_base"
}

/**
 * Shared Bugsnag options used by both server (instrumentation.ts) and client
 * (instrumentation-client.ts). Returns null when the API key is missing so
 * callers can skip initialization in local dev / tests without hard failure.
 */
export function getBugsnagConfig(): Config | null {
  const apiKey = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY
  if (!apiKey) return null

  const releaseStage =
    process.env.NEXT_PUBLIC_BUGSNAG_RELEASE_STAGE || process.env.NODE_ENV || "development"

  return {
    apiKey,
    releaseStage,
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION || undefined,
    enabledReleaseStages: undefined,
    // Drop the Node webstreams post-abort teardown race (nodejs/node#62036) on
    // the process-level capture path too: when the orphaned microtask rejects
    // outside any request scope, it bypasses onRequestError and reaches the
    // notifier as an unhandledRejection. The predicate requires node:internal/
    // frames, so it never matches in the browser — inert in the client bundle.
    onError: (event) => {
      if (isNodeWebstreamsTeardownError(event.originalError)) return false
      // Discard known-benign browser noise (ResizeObserver loop reports,
      // exact connectivity-drop TypeErrors). Matches on the normalized
      // class + message so it covers both window.onerror auto-detection and
      // manual error-boundary notify() — both paths produced dashboard noise.
      const primaryError = event.errors[0]
      if (isIgnorableBrowserNoise(primaryError?.errorClass, primaryError?.errorMessage)) {
        return false
      }
      // A stack overflow whose every frame lies outside our /_next/ bundles was
      // thrown by browser-injected code (e.g. Chrome page translation on iOS).
      // console.warn keeps a trace in the browser console without a report.
      if (
        isInjectedScriptStackOverflow(
          primaryError?.errorClass,
          primaryError?.errorMessage,
          primaryError?.stacktrace,
        )
      ) {
        console.warn("[bugsnag] dropped stack overflow from browser-injected script")
        return false
      }
      // Hydration mismatches arrive minified ("Minified React error #418",
      // args[]=HTML) with nothing to triage on. Attach route + the client-only
      // checkout state + the DOM's top-level shape so a report can be told apart
      // from an extension-injected node. Privacy rules and the exact allowed
      // fields live in hydration-context.ts — read them before widening.
      if (isHydrationMismatchError(primaryError?.errorMessage)) {
        const hydration = collectHydrationContext()
        if (hydration) event.addMetadata("hydration", hydration)
      }
    },
    redactedKeys: [
      "password",
      "password_confirmation",
      "current_password",
      "api_key",
      "api_token",
      /^authorization$/i,
      /cookie/i,
      "secret",
      "token",
      "session",
    ],
    metadata: {
      customer: {
        theme: getThemeId(),
        siteUrl: process.env.NEXT_PUBLIC_SITE_URL,
      },
    },
  }
}
